import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField, makeStyles } from '@applyboard/ui-components';
import { ChevronDownOutlineIcon } from '@applyboard/ui-icons';
import styled from '@emotion/styled';

export function DropdownField<
  TValueType, // eslint-disable-line @typescript-eslint/no-explicit-any
  TOptionType extends { value: TValueType; label: string },
>(props: {
  onChange: (newValue: TValueType) => void;
  onSearchTextChange?: (newValue: string) => void;
  value: TValueType | null;
  options: Array<TOptionType>;
  label: string;
  error?: boolean;
  helperText?: string;
  mt?: number | number[];
  mb?: number | number[];
  mr?: number | number[];
  ml?: number | number[];
  required?: boolean;
  disabled?: boolean;
  highlightSearchText?: boolean;
  getGroupLabel?: (groupKey: string) => string;
  groupBy?: (option: TOptionType) => string;
  loading?: boolean;
  disableFiltering?: boolean;
  disableClearable?: boolean;
  currentOption?: { label: string; value: string | number };
  bottomText?: string;
}) {
  const selectedOption = useMemo(() => {
    if (props.currentOption && props.currentOption.value === props.value) {
      return props.currentOption;
    }

    return props.options.find(option => option.value === props.value);
  }, [props.options, props.value, props.currentOption]);

  const lastTruthySelectedOption = useLastTruthyValue(selectedOption);

  const useStyles = makeStyles({
    option: {
      backgroundColor: 'white',
    },
  });
  const autocompleteStyles = useStyles();
  const [inputValue, setInputValue] = useState('');
  const isLastOption = React.useRef(Symbol('isLastOption')).current;

  useEffect(() => {
    if (props.value === null || typeof props.value === 'undefined') {
      setInputValue('');
    } else if (lastTruthySelectedOption?.label && lastTruthySelectedOption?.value === props.value) {
      setInputValue(lastTruthySelectedOption.label);
    }
  }, [props.value, lastTruthySelectedOption?.label]);

  if (typeof props.bottomText === 'string' && !props.disableFiltering) {
    throw new Error(
      'props.bottomText currently only works if props.disableFiltering is set to true',
    );
  }

  return (
    <Autocomplete
      classes={{
        option: autocompleteStyles.option,
      }}
      onChange={(e, newSelectedOption) => {
        props.onChange(
          typeof newSelectedOption?.value === 'undefined' ? null : newSelectedOption.value,
        );
      }}
      options={[
        ...props.options,
        {
          [isLastOption]: true,
          value: '',
          label: '',
        },
      ]}
      value={selectedOption || null}
      clearOnBlur
      openOnFocus
      getOptionLabel={option => option?.label}
      getOptionDisabled={option => option?.disabled}
      filterOptions={props.disableFiltering ? options => options : undefined}
      inputValue={inputValue}
      disableClearable={props.disableClearable}
      renderInput={inputProps => (
        <TextField
          {...inputProps}
          label={props.label}
          variant="outlined"
          error={props.error}
          helperText={props.helperText}
          required={props.required}
          inputProps={{
            ...inputProps.inputProps,
            onChange: (e, ...otherArgs) => {
              inputProps.inputProps.onChange(e, ...otherArgs);
              setInputValue(e.currentTarget.value);
              props.onSearchTextChange?.(e.currentTarget.value);
            },
            onBlur: (...args) => {
              inputProps.inputProps.onBlur(...args);
              if (
                props.value !== null &&
                typeof props.value !== 'undefined' &&
                lastTruthySelectedOption &&
                lastTruthySelectedOption.value === props.value
              ) {
                setInputValue(lastTruthySelectedOption.label);
              }
            },
          }}
        />
      )}
      renderOption={props.highlightSearchText ? highlightOptionText : undefined}
      groupBy={option => {
        if (option[isLastOption]) {
          return 'lastoption';
        }

        if (props.groupBy) {
          return `group-${props.groupBy(option)}`;
        }

        return 'nogroup';
      }}
      renderGroup={(option: { group: string; children: React.ReactNode }) => {
        if (option.group === 'lastoption') {
          return props.bottomText ? <BottomText>{props.bottomText}</BottomText> : null;
        }

        if (option.group === 'nogroup') {
          return <>{option.children}</>;
        }

        return (
          <>
            <GroupHeading>{option.group.replace('group-', '')}</GroupHeading>
            {option.children}
          </>
        );
      }}
      freeSolo={false}
      mt={props.mt}
      mb={props.mb}
      mr={props.mr}
      ml={props.ml}
      fullWidth
      popupIcon={<ChevronDownOutlineIcon />}
      disabled={props.disabled}
      loading={props.loading}
    />
  );
}

const BottomText = styled.div({
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingTop: '16px',
  paddingBottom: '8px',
  borderTop: `1px solid #B9BFD5`,
  backgroundColor: 'white',
});

const highlightOptionText = (option, { inputValue }) => {
  const label = option?.label;
  if (!inputValue || !label) {
    return <div>{option?.label}</div>;
  }

  const searchRegex = new RegExp(escapeRegex(inputValue), 'i');
  const termPosition = label.search(searchRegex);
  const matchedTerm = label.substring(termPosition, termPosition + inputValue.length);
  const parts = [];

  if (termPosition > 0) {
    parts.push(label.substring(0, termPosition));
  }
  parts.push(matchedTerm);
  parts.push(label.substring(termPosition + inputValue.length));

  return (
    <div>
      {parts.map((part, index) => (
        <span key={index} style={{ fontWeight: searchRegex.test(part) ? 600 : 400 }}>
          {part}
        </span>
      ))}
    </div>
  );
};

function escapeRegex(string) {
  return string.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'); // eslint-disable-line no-useless-escape
}

const GroupHeading = styled.li({
  fontFamily: 'Montserrat',
  fontWeight: 600,
  fontSize: 14,
  textTransform: 'uppercase',
  padding: 16,
  backgroundColor: 'white',
});

function useLastTruthyValue<T>(value: T): T {
  // eslint-disable-line @typescript-eslint/no-explicit-any
  const ref = React.useRef(value);

  if (value) {
    ref.current = value;
  }

  return ref.current;
}
